'use client';

import { motion } from 'motion/react';
import { useLocale } from './LocaleProvider';
import { dict, t } from '@/lib/i18n';

const brands = [
  {
    name: 'Hermès',
    emoji: '🧡',
    trend: '+15%',
    trendUp: true,
    items: { en: 'Birkin · Kelly · Constance', ar: 'بِركن · كيلي · كونستانس' },
    note: { en: 'Appreciates above retail', ar: 'تزداد قيمتها فوق سعر التجزئة' },
  },
  {
    name: 'Chanel',
    emoji: '🖤',
    trend: '+12%',
    trendUp: true,
    items: { en: 'Classic Flap · Boy · 2.55', ar: 'كلاسيك فلاب · بوي · 2.55' },
    note: { en: 'Annual price increases', ar: 'زيادات سنوية في الأسعار' },
  },
  {
    name: 'Rolex',
    emoji: '⌚',
    trend: '+10%',
    trendUp: true,
    items: { en: 'Submariner · Daytona · GMT', ar: 'سبمارينر · دايتونا · GMT' },
    note: { en: 'Most models above retail', ar: 'معظم الموديلات فوق التجزئة' },
  },
  {
    name: 'Louis Vuitton',
    emoji: '🤎',
    trend: '+6%',
    trendUp: true,
    items: { en: 'Neverfull · Speedy · Alma', ar: 'نيفرفول · سبيدي · ألما' },
    note: { en: 'Strong monogram demand', ar: 'طلب قوي على المونوغرام' },
  },
  {
    name: 'Dior',
    emoji: '🩶',
    trend: '+4%',
    trendUp: true,
    items: { en: 'Lady Dior · Saddle · Book Tote', ar: 'ليدي ديور · سادل · بوك توت' },
    note: { en: 'Steady luxury hold', ar: 'ثبات فاخر' },
  },
  {
    name: 'Gucci',
    emoji: '💚',
    trend: '-5%',
    trendUp: false,
    items: { en: 'Dionysus · Marmont · Jackie', ar: 'ديونيسوس · مارمونت · جاكي' },
    note: { en: 'Depreciates — buy pre-owned', ar: 'تنخفض — اشترِ مستعملة' },
  },
];

export function TrendingBrands() {
  const { locale } = useLocale();

  return (
    <section className="relative py-14 md:py-28 border-t border-white/5">
      <div className="mx-auto max-w-7xl px-5 sm:px-6">
        <div className="max-w-2xl mb-16">
          <div className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-gold-soft font-semibold mb-4">
            <span className="w-1 h-1 rounded-full bg-gold-soft" />
            {t(dict.trending.eyebrow, locale)}
          </div>
          <h2 className="font-serif text-display-sm text-cream leading-[0.98]">
            {t(dict.trending.heading, locale)}
          </h2>
          <p className="mt-4 text-fog-500 text-sm">
            {t(dict.trending.subheading, locale)}
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {brands.map((brand, i) => (
            <motion.div
              key={brand.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              className="group rounded-luxe bg-ink-800/60 border border-white/5 p-5 hover:border-gold/20 transition"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{brand.emoji}</span>
                  <div>
                    <div className="text-fog-100 font-semibold">{brand.name}</div>
                    <div className="text-xs text-fog-500">
                      {t(brand.items, locale)}
                    </div>
                  </div>
                </div>
                <span
                  className={`text-xs font-mono font-bold px-2 py-1 rounded ${
                    brand.trendUp
                      ? 'bg-emerald-950/50 text-emerald-400'
                      : 'bg-red-950/50 text-red-400'
                  }`}
                >
                  {brand.trend}
                </span>
              </div>
              <div className="text-xs text-fog-500 mt-2">
                {t(brand.note, locale)}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
